import type { ReactNode } from 'react';
import type { IconName } from '../icons';
import { Icon } from './Icon';
import { ProgressBar } from './ProgressBar';

// Card / Surface (Authority §25.4 / §26): white, 12px radius, 1px hairline, 0 1px 8px shadow,
// 16px padding. `tone="low"` is the tinted AI / hint block (02 / 04 / 05) with no hairline.
export function Card(props: {
  children: ReactNode;
  tone?: 'surface' | 'low';
  tight?: boolean;
  flush?: boolean;
  className?: string;
  testId?: string;
}) {
  const className = ['card', props.tone === 'low' ? 'card-low' : '', props.tight ? 'card-tight' : '', props.flush ? 'card-flush' : '', props.className ?? '']
    .filter((c) => c.length > 0)
    .join(' ');
  return (
    <section className={className} data-testid={props.testId}>
      {props.children}
    </section>
  );
}

// Phase 1 alias: titled card with an optional right-side action.
export function SectionCard(props: { title?: string; right?: ReactNode; children: ReactNode; className?: string; testId?: string }) {
  return (
    <section className={`card ${props.className ?? ''}`} data-testid={props.testId}>
      {props.title ? (
        <div className="card-head">
          <p className="card-title">{props.title}</p>
          {props.right ? <div className="card-head-right">{props.right}</div> : null}
        </div>
      ) : null}
      {props.children}
    </section>
  );
}

// Large selectable option card (task book §8 start mode / §8 A1-A2 processing choice): icon tile ·
// title · body · optional status line; selected = primary ring. One button, ≥ 72px tall.
export function ChoiceCard(props: {
  icon: IconName;
  title: string;
  body: string;
  status?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={`choice-card ${props.selected ? 'choice-card-selected' : ''}`}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-pressed={props.selected}
      data-testid={props.testId}
    >
      <span className={`icon-tile ${props.selected ? '' : 'icon-tile-soft'}`}>
        <Icon name={props.icon} />
      </span>
      <span className="choice-card-main">
        <span className="choice-card-title">{props.title}</span>
        <span className="choice-card-body">{props.body}</span>
        {props.status ? <span className="choice-card-status">{props.status}</span> : null}
      </span>
      <Icon name={props.selected ? 'check_circle' : 'chevron_right'} className="choice-card-trailing" />
    </button>
  );
}

// 11–12px uppercase tracked label (AREA CHECKLIST · ITEM STATUS · FINDING TITLE).
export function SectionEyebrow(props: { children: ReactNode; tone?: 'default' | 'primary'; className?: string }) {
  return <p className={`eyebrow ${props.tone === 'primary' ? 'eyebrow-primary' : ''} ${props.className ?? ''}`}>{props.children}</p>;
}

// Section header: title (18px) · optional meta line · optional trailing action (02 "Area Photos (3)").
export function SectionHeader(props: { title: ReactNode; meta?: ReactNode; eyebrow?: string; action?: ReactNode; testId?: string }) {
  return (
    <div className="section-header" data-testid={props.testId}>
      <div className="section-header-main">
        {props.eyebrow ? <SectionEyebrow>{props.eyebrow}</SectionEyebrow> : null}
        <h2 className="section-title">{props.title}</h2>
        {props.meta ? <p className="section-meta">{props.meta}</p> : null}
      </div>
      {props.action ? <div className="section-header-action">{props.action}</div> : null}
    </div>
  );
}

// iPad pane header (04 left "AREA CHECKLIST / Kitchen / 7/12 Completed", 05 "AREA / Kitchen / 45%"):
// eyebrow · large title · optional meta and progress. Tinted variant for the 05 left pane.
export function PaneHeader(props: {
  eyebrow: string;
  title: string;
  meta?: ReactNode;
  progress?: { value: number; total: number; label?: string };
  tint?: boolean;
  action?: ReactNode;
  testId?: string;
}) {
  return (
    <header className={`pane-header ${props.tint ? 'pane-header-tint' : ''}`} data-testid={props.testId}>
      <div className="pane-header-main">
        <div className="pane-header-text">
          <SectionEyebrow>{props.eyebrow}</SectionEyebrow>
          <h1 className="pane-header-title">{props.title}</h1>
          {props.meta ? <p className="pane-header-subtitle">{props.meta}</p> : null}
          {props.progress ? (
            <div className="pane-header-progress">
              <ProgressBar value={props.progress.value} total={props.progress.total} label={props.progress.label} slim />
            </div>
          ) : null}
        </div>
      </div>
      {props.action ? <div className="pane-header-side">{props.action}</div> : null}
    </header>
  );
}
